import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/global-hooks/useAuth";
import { LockIcon, CheckCircleIcon, XCircleIcon, Loader2 } from "lucide-react";
import { ModalShell } from "@/components/ui/modal-shell";
import { getSafeErrorMessage } from "@/lib/utils";

interface ChangePasswordModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export function ChangePasswordModal({ isOpen, onClose }: ChangePasswordModalProps) {
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const { changePassword, logout } = useAuth();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (newPassword !== confirmPassword) {
			setError("Mật khẩu mới và xác nhận mật khẩu không khớp");
			return;
		}

		if (newPassword.length < 8) {
			setError("Mật khẩu mới phải có ít nhất 8 ký tự");
			return;
		}

		setIsLoading(true);

		try {
			const result = await changePassword(currentPassword, newPassword, true);

			if (result.error) {
				// `getSafeErrorMessage`, không phải `String(...)`: `authClient` trả về
				// object thường `{ message, code, status }` chứ không phải `Error`, nên
				// `String()` cho ra đúng chữ "[object Object]" trên màn hình người dùng.
				setError(getSafeErrorMessage(result.error));
			} else {
				setSuccess(true);
				// Logout immediately after successful password change
				// `logout` reloads the page, so it goes last — anything after it
				// would never run.
				setTimeout(async () => {
					onClose();
					resetForm();
					await logout();
				}, 1500);
			}
		} catch {
			setError("Đã xảy ra lỗi khi đổi mật khẩu");
		} finally {
			setIsLoading(false);
		}
	};

	const resetForm = () => {
		setCurrentPassword("");
		setNewPassword("");
		setConfirmPassword("");
		setError(null);
		setSuccess(false);
	};

	const handleClose = () => {
		if (!isLoading && !success) {
			resetForm();
			onClose();
		}
	};

	return (
		<ModalShell
			isOpen={isOpen}
			onClose={handleClose}
			title="Đổi mật khẩu"
			closeButton="none"
			closeOnBackdrop
			onSubmit={handleSubmit}
			icon={
				<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-950">
					<LockIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
				</div>
			}
			footer={
				<div className="flex flex-col gap-3">
					{!success && (
						<>
							<Button
								type="submit"
								disabled={isLoading || !currentPassword || !newPassword || !confirmPassword}
								className="w-full h-11 font-medium"
							>
								{isLoading ? (
									<>
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										Đang xử lý...
									</>
								) : (
									"Đổi mật khẩu"
								)}
							</Button>
							<Button
								type="button"
								variant="outline"
								onClick={handleClose}
								disabled={isLoading}
								className="w-full h-11 font-medium"
							>
								Hủy
							</Button>
						</>
					)}

					{success && (
						<Button disabled className="w-full h-11 font-medium bg-green-600 hover:bg-green-600">
							Đang đăng xuất...
						</Button>
					)}
				</div>
			}
		>
			<div>
				{/* Success Alert */}
				{success && (
					<div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg dark:bg-green-950 dark:border-green-800">
						<div className="flex items-start gap-3">
							<CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
							<div className="text-sm text-green-800 dark:text-green-300 leading-relaxed">
								<p className="font-medium mb-1">Đổi mật khẩu thành công!</p>
								<p>Đang đăng xuất tất cả phiên đăng nhập...</p>
							</div>
						</div>
					</div>
				)}

				{/* Error Alert */}
				{error && (
					<div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-950 dark:border-red-800">
						<div className="flex items-start gap-3">
							<XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
							<p className="text-sm text-red-800 dark:text-red-300 leading-relaxed">{error}</p>
						</div>
					</div>
				)}

				{/* Form fields — thẻ <form> do ModalShell dựng, để nút submit ở
					    footer cố định vẫn thuộc cùng form. */}
				<div className="space-y-5">
					<div>
						<label
							htmlFor="current-password"
							className="block text-sm font-medium text-muted-foreground mb-2"
						>
							Mật khẩu hiện tại
						</label>
						<Input
							id="current-password"
							type="password"
							value={currentPassword}
							onChange={(e) => setCurrentPassword(e.target.value)}
							placeholder="Nhập mật khẩu hiện tại"
							required
							disabled={isLoading || success}
							className="h-11"
						/>
					</div>

					<div>
						<label
							htmlFor="new-password"
							className="block text-sm font-medium text-muted-foreground mb-2"
						>
							Mật khẩu mới
						</label>
						<Input
							id="new-password"
							type="password"
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							placeholder="Nhập mật khẩu mới (ít nhất 8 ký tự)"
							required
							disabled={isLoading || success}
							minLength={8}
							className="h-11"
						/>
					</div>

					<div>
						<label
							htmlFor="confirm-password"
							className="block text-sm font-medium text-muted-foreground mb-2"
						>
							Xác nhận mật khẩu mới
						</label>
						<Input
							id="confirm-password"
							type="password"
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							placeholder="Nhập lại mật khẩu mới"
							required
							disabled={isLoading || success}
							minLength={8}
							className="h-11"
						/>
					</div>
				</div>
			</div>
		</ModalShell>
	);
}
